import {
  Button,
  Fade,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { useApplyJob } from "../../api/effects/mutations/applyJob";
import useFetchJobQuestion from "../../api/effects/queries/Job/useFetchJobQuestion";
import HeaderGeneral from "../../components/Header/HeaderGeneral";
import JobCard from "../../components/JobCard";
import { WarningModal } from "../../components/WarningModal";
import { JobQuestionResponse } from "../../models/Job";
import ProfileState from "../../recoil/ProfileState";
import separateNumber, { formatNumber } from "../../utils/formatPrice";

const SquareGrid = styled(Grid)(({ theme }) => ({
  backgroundColor: "#FFF",
  height: "auto",
  width: "100%",
  boxShadow: "1px 1px 10px #888888",
  borderRadius: "10px",
  padding: "1%",
  [theme.breakpoints.down("sm")]: {
    padding: "10px",
  },
  margin: "1%",
}));

const DescGrid = styled(Grid)(({ theme }) => ({
  padding: "1%",
}));

const RootGrid = styled(Grid)(({ theme }) => ({
  paddingTop: "3%",
  width: "80%",
  [theme.breakpoints.down("sm")]: {
    width: "90%",
  },
}));

const FullSelect = styled(Select)(({ theme }) => ({
  width: "100%",
}));
const FullTextField = styled(TextField)(({ theme }) => ({
  width: "100%",
}));

const ClearLink = styled(Link)({
  textDecoration: "none",
  textTransform: "none",
});

const RoundedButton = styled(Button)(({ theme }) => ({
  width: "15%",
  [theme.breakpoints.down("sm")]: {
    width: "30%",
  },
  borderRadius: "90px",
  boxShadow: "none",
}));

const BaseGrid = styled(Grid)(({ theme }) => ({
  backgroundImage: `url(${process.env.PUBLIC_URL}/images/BackgroundJob.svg)`,
  backgroundRepeat: "no-repeat",
  backgroundSize: "100%",
  [theme.breakpoints.down("sm")]: {
    backgroundSize: "cover",
  },
}));

export default function JobApplyPage() {
  const { JobID } = useParams();

  const recoilValue = useRecoilValue(ProfileState);

  const { data: JobData, isSuccess } = useFetchJobQuestion(JobID!);

  const [jobQuestionData, setJobQuestionData] = useState<JobQuestionResponse>();

  const [init, setInit] = useState(true);

  useEffect(() => {
    if (JobData !== undefined && init) {
      setJobQuestionData(JobData);
      setInit(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JobData]);

  const [warningModalState, setWarningModalState] = useState(false);
  const [warningModalText, setWarningModalText] = useState("");

  const [requestGaji, setRequestGaji] = useState("");
  const [availability, setAvailability] = useState("");
  const [availabilityOther, setAvailabilityOther] = useState("");
  const [uraian, setUraian] = useState("");

  const [profileComplete, setProfileComplete] = useState(
    recoilValue?.Data === false ||
      recoilValue?.Pendidikan === false ||
      recoilValue?.Keluarga === false ||
      recoilValue?.Questionnaire === false
  );

  useEffect(() => {
    if (recoilValue !== undefined) {
      setProfileComplete(
        recoilValue?.Data === false ||
          recoilValue?.Pendidikan === false ||
          recoilValue?.Keluarga === false ||
          recoilValue?.Questionnaire === false
      );
    }
  }, [recoilValue]);

  const navigate = useNavigate();

  const { mutateAsync: ApplyJob } = useApplyJob({
    onSuccess() {
      setWarningModalText("Apply Success");
      setWarningModalState(true);
    },
    onError() {
      setWarningModalText("Apply Error");
      setWarningModalState(true);
    },
  });

  const ApplyJobPush = () => {
    if (formatNumber(requestGaji) < 1) {
      setWarningModalText("Mohon Isi Gaji");
      setWarningModalState(true);
      return;
    }
    if (availability.length < 1) {
      setWarningModalText("Mohon Isi Ketersediaan Waktu Bergabung");
      setWarningModalState(true);
      return;
    }
    if (uraian.length < 1) {
      setWarningModalText("Mohon Isi Uraian");
      setWarningModalState(true);
      return;
    }

    if (availability === "Lainnya" && availabilityOther.length < 1) {
      setWarningModalText("Mohon Jelaskan Ketersediaan Waktu Bergabung");
      setWarningModalState(true);
      return;
    }

    if (JobID !== undefined) {
      ApplyJob({
        JOB_ID: JobID,
        REQUEST_GAJI: formatNumber(requestGaji),
        AVAILABILITY:
          availability === "Lainnya" ? availabilityOther : availability,
        URAIAN: uraian,
      });
    }
  };

  return (
    <>
      <WarningModal
        open={warningModalState}
        handleClose={() => {
          setWarningModalState(false);
          if (warningModalText === "Apply Success") {
            navigate("/Profile");
          }
        }}
        text={warningModalText}
      />
      <HeaderGeneral />
      <BaseGrid container justifyContent="center">
        <Fade in={isSuccess}>
          <RootGrid container justifyContent="center">
            <Grid container justifyContent="center">
              {jobQuestionData === undefined || JobID === undefined ? (
                <span />
              ) : (
                <JobCard
                  Job={{
                    JOB_ID: JobID,
                    JOB: jobQuestionData.job[0].JOB,
                    DIVISI: jobQuestionData.job[0].DIVISI,
                    TANGGAL_DIBUAT: jobQuestionData.job[0].TANGGAL_DIBUAT,
                    LOKASI: jobQuestionData.job[0].LOKASI,
                    STATUS_KARYAWAN: jobQuestionData.job[0].STATUS_KARYAWAN,
                  }}
                  ButtonLink={`/Job/${JobID}`}
                  ButtonText="Apply"
                  BigTitle
                  ButtonHidden
                  Sizexs={12}
                  Sizemd={12}
                  Sizelg={12}
                />
              )}
            </Grid>
            <Grid container justifyContent="center">
              <SquareGrid container>
                <DescGrid item xs={12} sx={{ textAlign: "center" }}>
                  {profileComplete ? (
                    <ClearLink to="/Profile">
                      <Typography color="error">
                        Mohon untuk melengkapi profil dengan data terbaru
                        terlebih dahulu
                      </Typography>
                    </ClearLink>
                  ) : (
                    <Typography color="error">
                      Mohon untuk menjawab semua pertanyaan dibawah
                    </Typography>
                  )}
                </DescGrid>
                <DescGrid container>
                  <DescGrid item xs={12}>
                    <Typography variant="h6">
                      Perkiraan Gaji Minimal yang diajukan
                    </Typography>
                  </DescGrid>
                  <DescGrid item xs={12}>
                    <FullTextField
                      disabled={profileComplete}
                      value={requestGaji}
                      variant="outlined"
                      onChange={(e) => {
                        setRequestGaji(separateNumber(e.target.value));
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">Rp.</InputAdornment>
                        ),
                      }}
                    />
                  </DescGrid>
                </DescGrid>
                <DescGrid container>
                  <DescGrid item xs={12}>
                    <Typography variant="h6">
                      Ketersediaan Waktu Bergabung
                    </Typography>
                  </DescGrid>
                  <DescGrid item xs={12}>
                    <FullSelect
                      disabled={profileComplete}
                      labelId="input-select-label"
                      id="input-select"
                      value={availability}
                      onChange={(e) => setAvailability(String(e.target.value))}
                    >
                      {["Secepatnya", "1 Bulan Pemberitahuan", "Lainnya"].map(
                        (value, key) => (
                          <MenuItem value={value} key={key}>
                            {value}
                          </MenuItem>
                        )
                      )}
                    </FullSelect>
                    {availability === "Lainnya" ? (
                      <>
                        <Typography sx={{ paddingTop: "2%" }}>
                          Jelaskan Kapan Ketersediaan Waktu Bergabung
                        </Typography>
                        <FullTextField
                          sx={{ paddingTop: "2%" }}
                          disabled={profileComplete}
                          multiline
                          value={availabilityOther}
                          variant="outlined"
                          onChange={(e) => {
                            setAvailabilityOther(e.target.value);
                          }}
                        />
                      </>
                    ) : (
                      <span />
                    )}
                  </DescGrid>
                </DescGrid>
                <DescGrid container>
                  <DescGrid item xs={12}>
                    <Typography variant="h6">
                      Uraikan tugas dan tanggung jawab di pekerjaan terakhir!
                    </Typography>
                  </DescGrid>
                  <DescGrid item xs={12}>
                    <FullTextField
                      disabled={profileComplete}
                      multiline
                      value={uraian}
                      variant="outlined"
                      onChange={(e) => {
                        setUraian(e.target.value);
                      }}
                    />
                  </DescGrid>
                </DescGrid>
                <DescGrid container justifyContent="flex-end">
                  {profileComplete ? (
                    <span />
                  ) : (
                    <RoundedButton variant="contained" onClick={ApplyJobPush}>
                      Apply Now
                    </RoundedButton>
                  )}
                </DescGrid>
              </SquareGrid>
            </Grid>
          </RootGrid>
        </Fade>
      </BaseGrid>
    </>
  );
}
